import { z } from 'zod';
import { NOTIFICATION_CHANNELS, PROOF_TYPES, ROLES } from '../constants.js';
import { idSchema, isoDateTime, pageQuerySchema, paginated } from './common.js';
/* --------------------------- Proof of delivery -------------------------- */
export const proofSchema = z.object({
    id: idSchema,
    jobId: idSchema,
    type: z.enum(PROOF_TYPES),
    /** Storage key/path — never expose absolute server paths to clients. */
    filePath: z.string().max(500),
    originalFilename: z.string().max(255),
    mimeType: z.string().max(120),
    sizeBytes: z.number().int().min(0),
    recipientName: z.string().max(200).nullish(),
    deliveredAt: isoDateTime.nullish(),
    notes: z.string().max(4000).nullish(),
    uploadedBy: idSchema,
    createdAt: isoDateTime,
});
/** Multipart fields accepted by POST /api/v1/proofs (file + these fields). */
export const proofMetaSchema = z.object({
    jobId: idSchema,
    type: z.enum(PROOF_TYPES).default('photo'),
    recipientName: z.string().trim().max(200).nullish(),
    deliveredAt: isoDateTime.nullish(),
    notes: z.string().max(4000).nullish(),
});
/* ------------------------------- Exception ------------------------------ */
export const exceptionSchema = z.object({
    id: idSchema,
    jobId: idSchema,
    type: z.enum(['delivery_failed', 'recipient_absent', 'damaged', 'refused', 'access_issue', 'other']),
    status: z.enum(['open', 'resolved']),
    notes: z.string().max(4000),
    reportedBy: idSchema,
    reportedAt: isoDateTime,
    resolvedBy: idSchema.nullish(),
    resolvedAt: isoDateTime.nullish(),
    resolution: z.string().max(2000).nullish(),
});
export const exceptionCreateSchema = z.object({
    jobId: idSchema,
    type: z
        .enum(['delivery_failed', 'recipient_absent', 'damaged', 'refused', 'access_issue', 'other'])
        .default('delivery_failed'),
    notes: z.string().trim().min(3).max(4000),
});
export const exceptionResolveSchema = z.object({
    resolution: z.string().trim().min(3).max(2000),
    /** Resume delivery attempt (back to In Progress) or cancel the job. */
    nextStatus: z.enum(['in_progress', 'cancelled']).default('in_progress'),
});
/* -------------------------------- Audit --------------------------------- */
export const auditEntrySchema = z.object({
    id: idSchema,
    actorUserId: idSchema.nullish(),
    actorRole: z.enum(ROLES).nullish(),
    action: z.string().min(1).max(120),
    entityType: z.string().min(1).max(60),
    entityId: z.string().min(1).max(64),
    before: z.unknown().nullish(),
    after: z.unknown().nullish(),
    reason: z.string().max(1000).nullish(),
    metadata: z.unknown().nullish(),
    ip: z.string().max(60).nullish(),
    createdAt: isoDateTime,
});
export const auditListQuerySchema = pageQuerySchema.extend({
    entityType: z.string().max(60).optional(),
    entityId: z.string().max(64).optional(),
    actorUserId: idSchema.optional(),
    action: z.string().max(120).optional(),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
});
export const auditListSchema = paginated(auditEntrySchema);
/* ----------------------------- Notification ----------------------------- */
export const notificationSchema = z.object({
    id: idSchema,
    userId: idSchema,
    type: z.string().min(1).max(60),
    title: z.string().min(1).max(200),
    body: z.string().max(2000),
    channel: z.enum(NOTIFICATION_CHANNELS),
    status: z.enum(['pending', 'sent', 'failed']),
    link: z.string().max(500).nullish(),
    jobId: idSchema.nullish(),
    readAt: isoDateTime.nullish(),
    sentAt: isoDateTime.nullish(),
    createdAt: isoDateTime,
});
export const notificationListQuerySchema = pageQuerySchema.extend({
    unreadOnly: z.coerce.boolean().default(false),
    channel: z.enum(NOTIFICATION_CHANNELS).optional(),
});
export const notificationListSchema = paginated(notificationSchema);
//# sourceMappingURL=operations.js.map